// src/pages/IssueReporting/components/TicketsTable.js
import React from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    IconButton,
    Tooltip,
    CircularProgress
} from '@mui/material';
import {
    ChevronRight as ChevronRightIcon,
    AlertTriangle
} from 'react-feather';
import { statusColors, defaultStatus, priorityColors, defaultPriority } from '../mockData';

const normalizeKey = (s) => s?.toString().toUpperCase().replace(/_/g, ' ') || '';

const formatStatus = (s) => {
    if (!s) return 'N/A';
    return s.replace(/_/g, ' ').toLowerCase().replace(/(^|\s)\w/g, c => c.toUpperCase());
};

const formatPriority = (s) => {
    if (!s) return 'N/A';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

const getStatusColor = (status) => statusColors[normalizeKey(status)] || defaultStatus;

const getPriorityColor = (priority) => priorityColors[formatPriority(priority)] || defaultPriority;

// Helper for formatting dates
const formatDate = (dateString) => {
    try {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.error("Date formatting error:", error);
        return 'Invalid date';
    }
};

// Import correct icon based on status
const getStatusIcon = (status) => {
    const statusInfo = getStatusColor(status);
    let Icon;

    switch(statusInfo.icon) {
        case 'AlertTriangle':
            Icon = AlertTriangle;
            break;
        case 'Clock':
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>schedule</span>;
            break;
        case 'CheckCircle':
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>check_circle</span>;
            break;
        case 'XCircle':
            Icon = props => <span className="material-icons" style={{fontSize: 14}}>cancel</span>;
            break;
        default:
            Icon = AlertTriangle;
    }

    return <Icon size={14} />;
};

const TicketsTable = ({
                          loading,
                          tickets,
                          filteredTickets,
                          paginatedTickets,
                          page,
                          rowsPerPage,
                          handleChangePage,
                          handleChangeRowsPerPage,
                          openTicketDialog
                      }) => {
    return (
        <Box
            sx={{
                borderRadius: '10px',
                border: '1px solid rgba(0, 0, 0, 0.08)',
                overflow: 'hidden',
                backgroundColor: 'white'
            }}
        >
            {loading ? (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4}}>
                    <CircularProgress size={40}/>
                </Box>
            ) : filteredTickets.length === 0 ? (
                <Box sx={{py: 6, textAlign: 'center'}}>
                    <AlertTriangle size={48} color="#9CA3AF"/>
                    <Typography variant="h6" sx={{mt: 2}}>No tickets found</Typography>
                    <Typography variant="body2" color="textSecondary" sx={{mt: 1}}>
                        {tickets.length > 0
                            ? 'Try changing your search filters'
                            : 'You haven\'t raised any support tickets yet'}
                    </Typography>
                    {tickets.length === 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{mt: 2, borderRadius: '8px', textTransform: 'none'}}
                            href="/tickets/new"
                        >
                            Report New Issue
                        </Button>
                    )}
                </Box>
            ) : (
                <>
                    <TableContainer>
                        <Table sx={{minWidth: 650}}>
                            <TableHead sx={{backgroundColor: '#F9FAFB'}}>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 600}}>Ticket ID</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Issue</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Category</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Priority</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Status</TableCell>
                                    <TableCell sx={{fontWeight: 600}}>Last Updated</TableCell>
                                    <TableCell align="right" sx={{fontWeight: 600}}>Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedTickets.map((ticket) => (
                                    <TableRow
                                        key={ticket.id}
                                        hover
                                        sx={{
                                            '&:last-child td, &:last-child th': {border: 0},
                                            cursor: 'pointer',
                                            '&:hover': {backgroundColor: '#F9FAFB'}
                                        }}
                                        onClick={() => openTicketDialog(ticket)}
                                    >
                                        <TableCell component="th" scope="row" sx={{fontWeight: 500}}>
                                            {ticket.id}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={ticket.description || ''} placement="top">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        maxWidth: 250,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}
                                                >
                                                    {ticket.title}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>{ticket.category}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatPriority(ticket.priority)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getPriorityColor(ticket.priority).bg,
                                                    color: getPriorityColor(ticket.priority).color,
                                                    borderRadius: '4px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(ticket.status)}
                                                label={formatStatus(ticket.status)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getStatusColor(ticket.status).bg,
                                                    color: getStatusColor(ticket.status).color,
                                                    borderRadius: '4px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 500
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(ticket.updatedAt)}</TableCell>
                                        <TableCell align="right">
                                            <IconButton size="small" onClick={(e) => {
                                                e.stopPropagation();
                                                openTicketDialog(ticket);
                                            }}>
                                                <ChevronRightIcon size={18}/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredTickets.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </>
            )}
        </Box>
    );
};

export default TicketsTable;